/*******************************************************************
* PAGE        : Users
* REMARKS     :
* CREATED ON  : 09-09-2026
* AUTHOR      : Bamroliya.Paras
* MODIFY      : Bamroliya.Paras
* MODIFY ON   : 09-09-2026
********************************************************************/

import React, { useEffect, useState } from 'react';
import NotificationComponent from '../../components/NotificationComponent/NotificationComponent';
import { TablePagination, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LoaderComponent from '../../components/LoaderComponent/LoaderComponent';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import filtericon from "../../assets/filter-icon.svg";
import editsquare from "../../assets/edit-square.svg";
import deleteicon from "../../assets/delete.svg";
import UserForm from './UserForm';
import { DeleteTicketUserMaster, GetTicketUserMaster } from '../../services/apiController';
import DialogComponent from '../../components/DialogComponent/DialogComponent';
import UserRightsForm from './UserRightsForm';

// #region Component
const Users = () => {

    // #region States
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const [openPopup, setOpenPopup] = useState(false);
    const [openPopupAction, setOpenPopupAction] = useState(0);
    const [selectedData, setSelectedData] = useState({});

    const [usersList, setUsersList] = useState([]);
    // State to manage snackbar visibility and messages
    const [snackbarStates, setSnackbarStates] = useState({ isSnackbarOpen: false, snackbarMessage: '' });
    const handleCloseSnackbar = () => {
        setSnackbarStates({ isSnackbarOpen: false, snackbarMessage: '', snackbarSeverity: '' });
    };

    const [rowsPerPage, setRowsPerPage] = useState(10); // Default rows per page
    const [currentPage, setCurrentPage] = useState(1); // Default current page
    const [maxRows, setMaxRows] = useState(0); // Total number of rows
    const [gotoPage, setGotoPage] = useState(1); // Page input for jump
    const [totalPages, setTotalPages] = useState(1); // Total pages calculation
    const [searchQuery, setSearchQuery] = useState(''); // For search query
    const [sortOrder, setSortOrder] = useState({ column: '', order: 'asc' }); // For sorting state
    const [columnCount, setColumnCount] = useState(1);
    // #endregion

    useEffect(() => {
        const thCount = document.querySelectorAll('thead th').length;
        setColumnCount(thCount || 1);
    }, []);

    useEffect(() => {
        wsGetTicketUserMaster()
    }, [rowsPerPage, currentPage]); // Re-run the effect when rowsPerPage or currentPage changes

    const wsGetTicketUserMaster = async () => {
        setLoading(true);
        GetTicketUserMaster({})
            .then((response) => {
                if (response.status) {
                    setUsersList(response.users);
                    setMaxRows(response?.users?.length); // Set total rows from response
                    setTotalPages(Math.ceil(response?.users?.length / rowsPerPage)); // Calculate total pages
                } else {
                    // Handle error scenario
                }
                setLoading(false);
            }).catch((error) => {
                setLoading(false);
                // Handle API error
            });
    }

    const wsDeleteTicketUserMaster = async (ticketUserMasterId) => {
        setLoading(true);
        const params = { ticketUserMasterId }

        DeleteTicketUserMaster(params).then((response) => {
            if (response.status) {
                setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: response?.message || "Rate delete successfully", snackbarSeverity: "success" });
            } else {
                setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: response.message, snackbarSeverity: 'error' });
            }
            handleClose();
            setLoading(false);
        }).catch((error) => {
            console.log('error:', error)
            setSnackbarStates({ isSnackbarOpen: true, snackbarMessage: "Something went wrong please try again", snackbarSeverity: 'error' });
            handleClose();
            setLoading(false);
        });
    }

    // #region Methods
    // Handle page change
    const handleChangePage = (user, newPage) => {
        setCurrentPage(newPage + 1); // MUI TablePagination uses 0-based index
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (user) => {
        setRowsPerPage(parseInt(user.target.value, 10));
        setCurrentPage(1); // Reset to page 1 when rows per page changes
    };

    // Handle page input change
    const handleGotoPageChange = (user) => {
        setGotoPage(user.target.value);
    };

    // Handle the submit (Enter key press) for "Go to page"
    const handleSubmitGotoPage = (user) => {
        if (user.key === 'Enter') {
            const pageNumber = Math.max(1, Math.min(totalPages, parseInt(gotoPage, 10))); // Ensure valid page
            setCurrentPage(pageNumber);
        }
    };

    // Handle search query change
    const handleSearchChange = (user) => {
        setSearchQuery(user.target.value);
    };

    // Filtered and sorted usersList
    const SEARCH_FIELDS = [
        'EventName',
        'EventDate',
        'EventDay'
    ];

    const filteredUsers = usersList?.filter((itemEvent) =>
        SEARCH_FIELDS.some((field) =>
            itemEvent[field]
                ?.toString()
                .toLowerCase()
                .includes(searchQuery.toLowerCase())
        )
    );


    // Sorting function
    const handleSort = (column) => {
        const newOrder = sortOrder.order === 'asc' ? 'desc' : 'asc';
        setSortOrder({
            column: column,
            order: newOrder,
        });
    };

    const sortedUsers = filteredUsers.sort((a, b) => {
        if (!sortOrder.column) return 0;

        const aValue = a[sortOrder.column];
        const bValue = b[sortOrder.column];

        if (aValue < bValue) return sortOrder.order === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder.order === 'asc' ? 1 : -1;
        return 0;
    });


    const handleDeleteRateCode = () => {
        wsDeleteTicketUserMaster(selectedData?.TicketUserMasterId)
    }

    const handleClose = () => {
        setOpenPopup(false);
        wsGetTicketUserMaster();
    }
    // #endregion

    return (loading ? <LoaderComponent />
        : <React.Fragment>
            <NotificationComponent isOpen={snackbarStates.isSnackbarOpen} message={snackbarStates.snackbarMessage} onClose={handleCloseSnackbar} severity={snackbarStates.snackbarSeverity} />

            <div style={{ padding: '10px 20px 50px', overflowX: 'auto' }}>
                <div className='table-heading-search' style={{ marginBottom: '10px', }}>
                    <h2 className='dialog-heading' style={{ marginBlockEnd: '0px' }}>Users List</h2>

                    {/* Search Input */}
                    <div className='table-heading-search' style={{ alignItems: 'center', gap: '16px' }}>
                        Search: <input type="text" value={searchQuery} onChange={handleSearchChange} style={{ padding: '5px', width: '200px', border: '1px solid #ccc', borderRadius: '4px', }} />
                        <ButtonComponent style={{ padding: "8px 16px", textTransform: 'none' }} onClick={() => { setOpenPopup(true); setOpenPopupAction(1); setSelectedData({}); }}>Add New User</ButtonComponent>
                    </div>

                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th className='tableHeaderStyle' onClick={() => handleSort('EventName')}>User Name</th>
                            <th className='tableHeaderStyle' onClick={() => handleSort('EventDate')}>Event Name</th>
                            <th className='tableHeaderStyle' onClick={() => handleSort('EventDate')} style={{ textAlign: 'center', width: '150px' }}>Is Active ?</th>
                            <th className='tableHeaderStyle' style={{ textAlign: 'center' }}><img src={filtericon} alt='filtericon' /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {!sortedUsers.length
                            ? <tr>
                                <td colSpan={columnCount} style={{ textAlign: 'center', padding: '10px', fontSize: '16px' }}>
                                    No Record Found ☹️
                                </td>
                            </tr>
                            : sortedUsers.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
                                .map((userItem, index) => (
                                    <tr key={index} style={{ ...tableRowStyle, backgroundColor: index % 2 === 1 ? '#ececec' : '#FFFFFF' }}>
                                        <td className='tableCellStyle'>{userItem.UserName}</td>
                                        <td className='tableCellStyle'>{userItem.EventName}</td>

                                        <td className="tableCellStyle" style={{ textAlign: 'center', width: '150px' }}>
                                            <span
                                                style={{
                                                    backgroundColor: userItem.IsActive ? '#d1fae5' : '#fee2e2',
                                                    color: userItem.IsActive ? '#065f46' : '#991b1b',
                                                    padding: '4px 16px',
                                                    borderRadius: '16px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                }}
                                            >
                                                {userItem.IsActive ? 'True' : 'False'}
                                            </span>
                                        </td>

                                        <td className='tableCellStyle' style={{ textAlign: 'center', width: '100px' }}>
                                            <Tooltip title="Edit User" arrow>
                                                <img src={editsquare} alt='editsquare' style={{ cursor: 'pointer' }} onClick={() => { setOpenPopup(true); setOpenPopupAction(3); setSelectedData(userItem); }} />
                                            </Tooltip>
                                            <Tooltip title="Delete Rate" arrow>
                                                <img src={deleteicon} alt='deleteicon' style={{ cursor: 'pointer', paddingLeft: '4px' }} onClick={() => { setOpenPopup(true); setOpenPopupAction(2); setSelectedData(userItem); }} />
                                            </Tooltip>
                                        </td>
                                    </tr>
                                ))}
                    </tbody>
                </table>

                {/* Pagination Controls */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '10px' }}>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100, 200, 500, 1000]}
                        component="div"
                        count={maxRows}
                        rowsPerPage={rowsPerPage}
                        page={currentPage - 1} // MUI TablePagination uses 0-based index
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        id="page-box"
                    />
                    <div style={{ fontSize: '13px', marginLeft: '10px' }}>
                        Page: <input style={{ width: '50px', border: '1px solid #ccc', borderRadius: '5px', padding: '4px' }} type="number" value={gotoPage} disabled={totalPages <= 1} onChange={handleGotoPageChange} onKeyDown={handleSubmitGotoPage} /> Of {totalPages}
                    </div>
                </div>
            </div>

            {/* Popup */}
            {openPopup && openPopupAction === 1 &&
                <UserForm
                    openPopup={openPopup}
                    selectedData={selectedData}
                    handleClose={handleClose} />
            }

            {openPopup && openPopupAction === 3 &&
                <UserRightsForm
                    openPopup={openPopup}
                    selectedData={selectedData}
                    handleClose={handleClose} />
            }

            {openPopup && openPopupAction === 2 &&
                <DialogComponent
                    isOpen={openPopup}
                    Title="Delete Rate"
                    ActionText={"Delete"}
                    IsLoading={loading}
                    onSave={handleDeleteRateCode}
                    onClose={handleClose}
                >
                    <label>Are you sure you want to delete this Rate ?</label>
                </DialogComponent>
            }
        </React.Fragment>
    );
};
// #endregion

const tableRowStyle = {
    borderBottom: '1px solid #eee',
};

export default Users;